import { FC, useCallback, useMemo, useRef, useState } from 'react';

import { VIEW_DATE_FORMAT } from 'utils/constants/date';
import { RequisiteName, RequisiteRole, RequisitesFormName } from 'utils/constants/requisites';
import { formatDate } from 'utils/date';

import {
    BottomSheet,
    createStaticDataProvider,
    Link,
    NavigationBar,
    RenderOptionType,
    Select,
    Text,
    TooltipHover,
    VSpacing,
} from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24, QuestionCircleOutlinedSize16 } from '@hh.ru/magritte-ui/icon';

import { formatToReactComponent } from 'bloko/common/trl';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { setAllRequisitesErrors, setRequisitesFromPayer } from 'store/models/requisites';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/RequisitesFormModal/RequisitesPayersSelect/RequisitesPayersSelect.module.less';

const TrlKeys = {
    label: 'RequisitesForm.RequisitesList.label',
    tooltip: {
        part1: 'RequisitesForm.tooltip.payer.part1',
        part2: 'RequisitesForm.tooltip.payer.part2',
    },
    placeholder: 'RequisitesForm.RequisitesList.placeholder',
    emailTitle: 'RequisitesForm.payer.emailTitle',
};

export const RequisitesPayersSelect: FC = () => {
    const payers = useSelector((state) => state.requisites.requisitesPayers);
    const { isAgency } = useSelector((state) => state.requisites.requisites);
    const email = useSelector((state) => state.clickme.email);

    const [bottomSheetVisible, setBottomSheetVisibleOn, setBottomSheetVisibleOff] = useOnOffState(false);

    const activatorRef = useRef<HTMLSpanElement>(null);

    const dataProvider = useMemo(
        () =>
            createStaticDataProvider(
                payers.map((requisites, index) => ({
                    value: index.toString(),
                    text: requisites.legalName ?? '',
                }))
            ),
        [payers]
    );

    const isAgencyRole = isAgency === RequisiteRole.Agency;

    const dispatch = useDispatch();

    const [selectValue, setSelectValue] = useState<{ value: string; text: string } | undefined>(undefined);

    const trl = useTrls();

    const hrefMailTo = `mailto:${email}?subject=${trl(TrlKeys.emailTitle)}`;

    const handleChange = useCallback(
        (payer: { value: string; text: string }) => {
            setSelectValue(payer);
            dispatch([
                setAllRequisitesErrors({}),
                setRequisitesFromPayer({
                    requisiteFormName: isAgencyRole
                        ? RequisitesFormName.AgencyRequisites
                        : RequisitesFormName.FinalAdvertiserRequisites,
                    payer: payers[Number(payer.value)],
                }),
            ]);
        },
        [dispatch, isAgencyRole, payers]
    );

    const tooltip = (
        <>
            <Text>{trl(TrlKeys.tooltip.part1)}</Text>
            <VSpacing default={10} />
            <Text>
                {formatToReactComponent(trl(TrlKeys.tooltip.part2), {
                    '[0]': (
                        <Link inline href={hrefMailTo} enableVisited={false}>
                            {email}
                        </Link>
                    ),
                })}
            </Text>
        </>
    );

    const customRenderItem: RenderOptionType<{ text: string; value: string }> = useCallback(
        ({ data: { text, value } }) => {
            const payer = payers[Number(value)];
            const inn = payer[RequisiteName.Inn];
            const agreementDate = payer[RequisiteName.AgreementDate];
            const agreementId = payer[RequisiteName.AgreementId];

            return (
                <div>
                    <Text>{text}</Text>

                    {inn && (
                        <>
                            <VSpacing default={10} />
                            <Text typography="label-4-regular" style="secondary">
                                {trl('RequisitesForm.RequisitesList.inn', [inn])}
                            </Text>
                        </>
                    )}
                    {agreementDate && (
                        <>
                            <VSpacing default={6} />
                            <Text typography="label-4-regular" style="secondary">
                                {trl('RequisitesForm.RequisitesList.agreement', [
                                    agreementId,
                                    formatDate(agreementDate, VIEW_DATE_FORMAT),
                                ])}
                            </Text>
                        </>
                    )}
                </div>
            );
        },
        [payers, trl]
    );

    if (payers.length === 0) {
        return null;
    }

    return (
        <div>
            <div className={styles.titleContainer}>
                <Text typography="label-2-regular" style="secondary">
                    {trl(TrlKeys.label)}
                </Text>
                <QuestionCircleOutlinedSize16
                    ref={activatorRef}
                    onClick={setBottomSheetVisibleOn}
                    initialColor="secondary"
                />
            </div>
            <VSpacing default={12} />
            <Select
                type="label"
                name="requisites-payers-select"
                value={selectValue}
                dataProvider={dataProvider}
                onChange={handleChange}
                triggerProps={{
                    size: 'medium',
                    label: trl(TrlKeys.placeholder),
                    stretched: true,
                    'data-qa': 'requisites-payers-select',
                }}
                renderDesktopItem={customRenderItem}
                bottomSheetHeight="content"
            />
            <VSpacing default={24} />

            <TooltipHover activatorRef={activatorRef} placement="right-center">
                {tooltip}
            </TooltipHover>

            <BottomSheet
                header={
                    <NavigationBar
                        title={trl(TrlKeys.label)}
                        right={<CrossOutlinedSize24 onClick={setBottomSheetVisibleOff} />}
                        showDivider="always"
                    />
                }
                visible={bottomSheetVisible}
                onClose={setBottomSheetVisibleOff}
            >
                {tooltip}
                <VSpacing default={16} />
            </BottomSheet>
        </div>
    );
};
